import React, { useEffect, useState } from "react";
import "../../index.css";
import { gql, useMutation } from "@apollo/client";
import { useAuthz } from "../../auth/useAuthz";
import Loader from "../../Loader";
import Modal from "../Model";
import RecordPaymentForm from "./RecordPaymentForm";
import { PAYMENT_METHODS, formatRupees, getAttachmentLink } from "./paymentQueries";

const DELETE_PAYMENT = gql`
  mutation deletePayment($id: ID!) {
    deletePayment(data: $id) {
      status
      message
    }
  }
`;

const MARK_PAYMENT_PAID = gql`
  mutation markPaymentPaid($id: ID!, $data: UpdatePaymentInput!) {
    updatePayment(id: $id, data: $data) {
      status
      message
    }
  }
`;

const DELETE_BILL_SETTLEMENT = gql`
  mutation deleteBillSettlement($id: ID!) {
    deleteBillSettlement(id: $id) {
      status
      message
    }
  }
`;

const PAGE_SIZE = 25;
const INVENTORY_PURCHASE_CATEGORY = "Inventory Purchase";

const isSelectableBill = (row) =>
  row.category === INVENTORY_PURCHASE_CATEGORY && !row.settlement;

// en-CA formats as YYYY-MM-DD in the admin's own timezone
const getToday = () => new Date().toLocaleDateString("en-CA");

const getPartyLabel = (category) => {
  if (category === "Inventory Purchase") return "Dealer";
  if (
    category === "Professional Salary" ||
    category === "Professional Advance Payment"
  ) {
    return "Professional";
  }
  if (category === "Admin Salary") return "Admin";
  return "Paid To";
};

const getPartyName = (row) =>
  row.dealerName || row.employeeName || row.adminName || "";

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-IN") : "-";

const toTime = (value) => (value ? new Date(value).getTime() : null);

const SORT_VALUES = {
  payer: (row) => (row.payerName || "").toLowerCase() || null,
  type: (row) => (row.type || "").toLowerCase() || null,
  amount: (row) => Number(row.amount),
  date: (row) => toTime(row.date),
  billDate: (row) => toTime(row.billDate),
  party: (row) => getPartyName(row).toLowerCase() || null,
  category: (row) => (row.category || "").toLowerCase() || null,
  method: (row) => (row.paymentMethod || "").toLowerCase() || null,
};

// Blank values stay at the bottom whichever way the column is sorted
const sortRows = (rows, { key, direction }) => {
  if (!key) return rows;

  const getValue = SORT_VALUES[key];
  const factor = direction === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    const left = getValue(a);
    const right = getValue(b);
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    if (left === right) return 0;
    return left > right ? factor : -factor;
  });
};

const downloadCsv = (rows, partyLabel) => {
  const header = [
    "Payer",
    "Type",
    "Amount",
    "Payment Date",
    "Bill Date",
    partyLabel,
    "Category",
    "Method",
    "Due",
    "Description",
  ];
  const lines = rows.map((row) => [
    row.payerName,
    row.type,
    row.amount,
    row.date ? formatDate(row.date) : "",
    row.billDate ? formatDate(row.billDate) : "",
    getPartyName(row),
    row.otherCategory ? `${row.category} (${row.otherCategory})` : row.category,
    row.paymentMethod,
    row.isPaymentDue ? "Yes" : "No",
    row.description,
  ]);
  const csv = [header, ...lines]
    .map((line) =>
      line.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","),
    )
    .join("\n");

  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = "Payments.csv";
  link.click();
  URL.revokeObjectURL(url);
};

const MarkPaidForm = ({ payment, onClose, onSuccess }) => {
  const [date, setDate] = useState(getToday());
  const [paymentMethod, setPaymentMethod] = useState("");
  const [markPaid, { loading }] = useMutation(MARK_PAYMENT_PAID);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!date || !paymentMethod) {
      alert("Please enter payment date and method");
      return;
    }

    try {
      const res = await markPaid({
        variables: {
          id: payment.id,
          data: {
            date: new Date(date).toISOString(),
            paymentMethod,
            isPaymentDue: false,
          },
        },
      });
      const result = res.data.updatePayment;

      if (result.status === "SUCCESS") {
        onClose();
        onSuccess?.();
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error("Mark as paid failed:", error);
      alert("Unable to mark as paid. Please try again.");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col space-y-4 text-gray-800">
      <h3 className="text-xl font-semibold text-[#FBB033] border-b pb-3">
        Mark as Paid
      </h3>
      <p className="text-sm text-gray-600">
        {getPartyName(payment) || payment.category}: ₹{" "}
        {Number(payment.amount).toLocaleString("en-IN")}
      </p>
      <div className="flex flex-col">
        <label className="text-sm mb-1 text-gray-600">Payment Date</label>
        <input
          type="date"
          required
          min="2020-01-01"
          max={getToday()}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-800 outline-none"
        />
      </div>
      <div className="flex flex-col">
        <label className="text-sm mb-1 text-gray-600">Payment Method</label>
        <select
          required
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-800 outline-none"
        >
          <option value="">Select</option>
          {PAYMENT_METHODS.map((method) => (
            <option key={method} value={method}>
              {method}
            </option>
          ))}
        </select>
      </div>
      <div className="flex justify-end gap-3 pt-3 border-t">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-600"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="bg-[#FBB033] px-5 py-2 rounded-md font-semibold text-black disabled:opacity-60"
        >
          Mark as Paid
        </button>
      </div>
    </form>
  );
};

const PaymentsTable = ({
  data,
  setOpenModal,
  setEditData,
  paymentsLoading,
  onDeleteSuccess,
  category,
}) => {
  const { can } = useAuthz();
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [sort, setSort] = useState({ key: null, direction: "asc" });
  const [page, setPage] = useState(1);
  const [markPaidRow, setMarkPaidRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isRecordingPayment, setIsRecordingPayment] = useState(false);

  const [deletePaymentMutation] = useMutation(DELETE_PAYMENT);
  const [deleteBillSettlement] = useMutation(DELETE_BILL_SETTLEMENT);

  useEffect(() => {
    setPage(1);
  }, [data, sort]);

  const partyLabel = getPartyLabel(category);
  const sortedRows = sortRows(data, sort);
  const pageCount = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const pageRows = sortedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (key) => {
    setSort((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const canLinkBills =
    category === INVENTORY_PURCHASE_CATEGORY && can("payments.edit");
  const selectedBills = data.filter((row) => selectedIds.includes(row.id));
  const selectedDealers = new Set(
    selectedBills.map((row) => row.dealerName?.trim().toLowerCase()),
  );
  const selectedPaidBill = selectedBills.find((row) => !row.isPaymentDue);

  const toggleBill = (rowId) => {
    setSelectedIds((current) =>
      current.includes(rowId)
        ? current.filter((id) => id !== rowId)
        : [...current, rowId],
    );
  };

  const handleUnlink = async (row) => {
    const confirmed = window.confirm(
      "Unlink these bills? Bills that were due will become due again.",
    );
    if (!confirmed) {
      return;
    }

    try {
      const res = await deleteBillSettlement({
        variables: { id: row.settlement.id },
      });
      const result = res.data.deleteBillSettlement;

      if (result.status === "SUCCESS") {
        onDeleteSuccess?.();
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error("Unlink failed:", error);
      alert("Unable to unlink the bills. Please try again.");
    }
  };

  const handleEditClick = (row) => {
    setOpenModal(true);
    setEditData(row);
  };

  const handleDeleteClick = async (row) => {
    const confirmed = window.confirm(
      `Delete payment of ₹ ${Number(row.amount).toLocaleString("en-IN")}?`,
    );

    if (!confirmed) {
      return;
    }

    try {
      const response = await deletePaymentMutation({
        variables: { id: row.id },
      });

      const result = response?.data?.deletePayment;

      if (result?.status === "SUCCESS") {
        alert("Payment deleted successfully");
        if (onDeleteSuccess) {
          onDeleteSuccess();
        }
        return;
      }

      alert(result?.message || "Failed to delete payment");
    } catch (error) {
      console.error("Delete payment failed:", error);
      alert("Unable to delete payment. Please try again.");
    }
  };

  const handleViewBillClick = (row) => {
    if (!row?.attachmentUrl) {
      alert("No attachment available for this payment");
      return;
    }

    window.open(
      getAttachmentLink(row.attachmentUrl),
      "_blank",
      "noopener,noreferrer",
    );
  };

  const formatType = (value) =>
    value
      ? `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`
      : "-";

  const toggleRowExpansion = (rowId) => {
    setExpandedRowId((currentRowId) => (currentRowId === rowId ? null : rowId));
  };

  const renderDueBadge = (row) =>
    row.isPaymentDue && (
      <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-500 text-black">
        Due
      </span>
    );

  const renderCategoryDetails = (row) => {
    if (row.category === "Inventory Purchase") {
      return (
        <div className="space-y-3">
          <p className="text-sm text-gray-300">
            <span className="font-semibold">Is GST Bill:</span>{" "}
            {row.isGstBill == true ? "Yes" : "No"}
          </p>
          <div>
            <p className="text-sm font-semibold text-gray-200 mb-2">
              Inventory Items
            </p>
            {Array.isArray(row.inventoryPayments) &&
            row.inventoryPayments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs text-left border border-gray-200 rounded-md">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      <th className="px-3 py-2 border-b">Category</th>
                      <th className="px-3 py-2 border-b">Qty</th>
                      <th className="px-3 py-2 border-b">Rate</th>
                      <th className="px-3 py-2 border-b">MRP</th>
                      <th className="px-3 py-2 border-b">Discount</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    {row.inventoryPayments.map((item) => (
                      <tr
                        key={
                          item.id || `${item.inventoryCategoryId}-${item.rate}`
                        }
                      >
                        <td className="px-3 py-2 border-b border-gray-100">
                          {item.inventoryCategory?.name ||
                            item.inventoryCategoryId ||
                            "-"}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100">
                          {item.quantity ?? "-"}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100">
                          {item.rate ?? "-"}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100">
                          {item.mrp ?? "-"}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100">
                          {item.discount ?? "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No inventory items found.</p>
            )}
          </div>
        </div>
      );
    }

    if (row.category === "Other Expenses") {
      return (
        <p className="text-sm text-gray-300">
          <span className="font-semibold">Expense Name:</span>{" "}
          {row.otherCategory || "-"}
        </p>
      );
    }

    return null;
  };

  const renderHeader = (key, label) => (
    <th className="text-gray-300 px-4 py-3 text-left whitespace-nowrap">
      <button
        type="button"
        onClick={() => handleSort(key)}
        className="font-semibold hover:text-[#FBB033]"
      >
        {label}
        {sort.key === key ? (sort.direction === "asc" ? " ▲" : " ▼") : ""}
      </button>
    </th>
  );

  const renderSettlement = (row) => {
    const { settlement } = row;

    return (
      <div className="mb-3 rounded-md border border-[#333] p-3 text-sm text-gray-300">
        <p className="font-semibold text-gray-200 mb-1">
          Part of a {formatRupees(settlement.amount)} payment on{" "}
          {formatDate(settlement.date)} ({settlement.paymentMethod})
        </p>
        {settlement.reference && <p>UTR / Reference: {settlement.reference}</p>}
        {settlement.payerName && <p>Paid by: {settlement.payerName}</p>}
        {settlement.description && <p>{settlement.description}</p>}
        <p className="mt-1">
          Bills:{" "}
          {settlement.bills
            .map(
              (bill) =>
                `${formatRupees(bill.amount)} (${formatDate(bill.billDate)})`,
            )
            .join(" + ")}
        </p>
        <div className="flex gap-4 mt-2">
          {settlement.attachmentUrl && (
            <a
              target="_blank"
              rel="noreferrer"
              href={getAttachmentLink(settlement.attachmentUrl)}
              className="text-[#FBB033] underline"
            >
              View Payment Proof
            </a>
          )}
          {can("payments.edit") && (
            <button
              type="button"
              onClick={() => handleUnlink(row)}
              className="text-red-400 underline"
            >
              Unlink
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderDetails = (row) => (
    <>
      {row.settlement && renderSettlement(row)}
      {row.description && (
        <p className="text-sm text-gray-300 mb-3">
          <span className="font-semibold">Description:</span> {row.description}
        </p>
      )}
      <p className="text-sm font-semibold text-gray-200 mb-2">
        Category Details
      </p>
      {renderCategoryDetails(row) || (
        <p className="text-sm text-gray-500">
          No additional details for this category.
        </p>
      )}
    </>
  );

  if (paymentsLoading) {
    return <Loader />;
  }

  return (
    <div className="flex flex-col space-y-4 w-full">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#FBB033]">Payments</h2>
        <button
          type="button"
          onClick={() => downloadCsv(sortedRows, partyLabel)}
          className="border border-[#FBB033] bg-black text-[#FBB033] font-medium text-sm rounded-md px-4 py-2"
        >
          Download CSV
        </button>
      </div>

      {selectedBills.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#FBB033] bg-amber-50 px-4 py-3 text-sm text-gray-800">
          <span className="font-semibold">
            {selectedBills.length} bills selected,{" "}
            {formatRupees(
              selectedBills.reduce((sum, row) => sum + Number(row.amount), 0),
            )}
            {selectedDealers.size > 1 && (
              <span className="ml-2 font-normal text-red-600">
                Select bills from one dealer only
              </span>
            )}
            {selectedBills.length === 1 && (
              <span className="ml-2 font-normal text-gray-500">
                Select at least 2 bills
              </span>
            )}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-600"
            >
              Clear
            </button>
            <button
              type="button"
              disabled={selectedBills.length < 2 || selectedDealers.size > 1}
              onClick={() => setIsRecordingPayment(true)}
              className="bg-[#FBB033] px-4 py-1.5 rounded-md font-semibold text-black disabled:opacity-50"
            >
              Record Payment
            </button>
          </div>
        </div>
      )}

      {/* Desktop: table */}
      <div className="hidden md:block rounded-lg overflow-x-auto bg-[#0c0b0b] p-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#1a1a1a] border-b border-[#333]">
              {canLinkBills && <th className="px-2 py-3" aria-label="Select" />}
              {renderHeader("payer", "Payer")}
              {renderHeader("type", "Type")}
              {renderHeader("amount", "Amount")}
              {renderHeader("date", "Payment Date")}
              {renderHeader("billDate", "Bill Date")}
              {renderHeader("party", partyLabel)}
              {renderHeader("category", "Category")}
              {renderHeader("method", "Method")}
              <th className="text-gray-300 px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {pageRows.map((row) => {
              const isCredit = row.type?.toLowerCase() === "credit";

              return (
                <React.Fragment key={row.id}>
                  <tr
                    className="border-b border-[#333] hover:bg-[#1a1a1a] transition cursor-pointer"
                    onClick={() => toggleRowExpansion(row.id)}
                  >
                    {canLinkBills && (
                      <td
                        className="px-2 py-3 text-center"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {isSelectableBill(row) && (
                          <input
                            type="checkbox"
                            aria-label="Select bill"
                            checked={selectedIds.includes(row.id)}
                            onChange={() => toggleBill(row.id)}
                          />
                        )}
                      </td>
                    )}
                    <td className="text-gray-300 px-4 py-3">
                      {row.payerName || "-"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 text-xs rounded-full font-semibold ${
                          isCredit
                            ? "bg-green-600 text-white"
                            : "bg-red-600 text-white"
                        }`}
                      >
                        {formatType(row.type)}
                      </span>
                    </td>
                    <td
                      className={`px-4 py-3 font-semibold whitespace-nowrap ${
                        isCredit ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      ₹ {Number(row.amount).toLocaleString("en-IN")}
                      {row.settlement && (
                        <span className="ml-2 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-blue-600 text-white">
                          Linked
                        </span>
                      )}
                    </td>
                    <td className="text-gray-300 px-4 py-3 whitespace-nowrap">
                      {row.date ? formatDate(row.date) : renderDueBadge(row) || "-"}
                    </td>
                    <td className="text-gray-300 px-4 py-3 whitespace-nowrap">
                      {formatDate(row.billDate)}
                    </td>
                    <td className="text-gray-300 px-4 py-3">
                      {getPartyName(row) || "-"}
                    </td>
                    <td className="text-gray-300 px-4 py-3">
                      {row.category}
                      {row.otherCategory ? ` (${row.otherCategory})` : ""}
                    </td>
                    <td className="text-gray-300 px-4 py-3">
                      {row.paymentMethod || "-"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center gap-2 flex-wrap">
                        {can("payments.edit") && row.isPaymentDue && (
                          <button
                            onClick={(event) => {
                              event.stopPropagation();
                              setMarkPaidRow(row);
                            }}
                            className="px-3 py-1 text-sm bg-[#FBB033] text-black rounded font-semibold whitespace-nowrap"
                          >
                            Mark Paid
                          </button>
                        )}
                        {!isCredit && (
                          <button
                            onClick={(event) => {
                              event.stopPropagation();
                              handleViewBillClick(row);
                            }}
                            className="px-3 py-1 text-sm bg-[#333] text-gray-200 rounded hover:bg-[#444] transition whitespace-nowrap"
                          >
                            View Bill
                          </button>
                        )}
                        {can("payments.edit") && !isCredit && (
                          <button
                            onClick={(event) => {
                              event.stopPropagation();
                              handleEditClick(row);
                            }}
                            className="px-3 py-1 text-sm bg-[#333] text-gray-200 rounded hover:bg-[#444] transition"
                          >
                            Edit
                          </button>
                        )}
                        {can("payments.delete") && !isCredit && (
                          <button
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDeleteClick(row);
                            }}
                            className="px-3 py-1 text-sm bg-white text-red-600 rounded hover:bg-red-50 transition"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {expandedRowId === row.id && (
                    <tr className="bg-[#1a1a1a] border-b border-[#333]">
                      <td colSpan={canLinkBills ? 10 : 9} className="px-4 py-4">
                        {renderDetails(row)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile: card view */}
      <div className="md:hidden flex flex-col gap-3">
        {pageRows.map((row) => {
          const isExpanded = expandedRowId === row.id;
          const isCredit = row.type?.toLowerCase() === "credit";

          return (
            <div
              key={row.id}
              className="relative bg-[#0c0b0b] border border-[#2a2a2a] rounded-xl p-4 shadow"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-white font-semibold text-sm break-words">
                  {canLinkBills && isSelectableBill(row) && (
                    <input
                      type="checkbox"
                      aria-label="Select bill"
                      className="mr-2"
                      checked={selectedIds.includes(row.id)}
                      onChange={() => toggleBill(row.id)}
                    />
                  )}
                  {row.payerName || "-"}
                  {row.settlement && (
                    <span className="ml-2 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-blue-600 text-white">
                      Linked
                    </span>
                  )}
                </span>
                <span
                  className={`shrink-0 px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                    isCredit
                      ? "bg-green-600 text-white"
                      : "bg-red-600 text-white"
                  }`}
                >
                  {formatType(row.type)}
                </span>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                {[
                  ["Payment Date", row.date ? formatDate(row.date) : renderDueBadge(row) || "-"],
                  ["Bill Date", formatDate(row.billDate)],
                  [partyLabel, getPartyName(row) || "-"],
                  [
                    "Category",
                    `${row.category}${row.otherCategory ? ` (${row.otherCategory})` : ""}`,
                  ],
                  ["Method", row.paymentMethod || "-"],
                ].map(([label, value]) => (
                  <div key={label}>
                    <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                      {label}
                    </div>
                    <div className="text-white break-words">{value}</div>
                  </div>
                ))}
                <div>
                  <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                    Amount
                  </div>
                  <div
                    className={`font-semibold ${
                      isCredit ? "text-green-500" : "text-red-500"
                    }`}
                  >
                    ₹ {Number(row.amount).toLocaleString("en-IN")}
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="mt-3 w-full flex items-center justify-center gap-1 text-xs font-semibold text-[#FBB033] border-t border-[#2a2a2a] pt-2"
                onClick={() => toggleRowExpansion(row.id)}
              >
                {isExpanded ? "Hide details ▲" : "View details ▼"}
              </button>

              {isExpanded && (
                <div className="mt-3 bg-[#1a1a1a] rounded-xl p-3 border border-[#2a2a2a]">
                  {renderDetails(row)}
                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#2a2a2a]">
                {can("payments.edit") && row.isPaymentDue && (
                  <button
                    onClick={() => setMarkPaidRow(row)}
                    className="flex-1 px-3 py-1.5 text-sm bg-[#FBB033] text-black rounded font-semibold whitespace-nowrap"
                  >
                    Mark Paid
                  </button>
                )}
                {!isCredit && (
                  <button
                    onClick={() => handleViewBillClick(row)}
                    className="flex-1 px-3 py-1.5 text-sm bg-[#333] text-gray-200 rounded hover:bg-[#444] transition whitespace-nowrap"
                  >
                    View Bill
                  </button>
                )}
                {can("payments.edit") && !isCredit && (
                  <button
                    onClick={() => handleEditClick(row)}
                    className="flex-1 px-3 py-1.5 text-sm bg-[#333] text-gray-200 rounded hover:bg-[#444] transition"
                  >
                    Edit
                  </button>
                )}
                {can("payments.delete") && !isCredit && (
                  <button
                    onClick={() => handleDeleteClick(row)}
                    className="flex-1 px-3 py-1.5 text-sm bg-white text-red-600 rounded hover:bg-red-50 transition"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {pageCount > 1 && (
        <div className="flex items-center justify-end gap-3 text-sm text-gray-200">
          <button
            type="button"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="px-3 py-1 border rounded disabled:opacity-40"
          >
            Previous
          </button>
          <span>
            Page {page} of {pageCount}
          </span>
          <button
            type="button"
            disabled={page === pageCount}
            onClick={() => setPage(page + 1)}
            className="px-3 py-1 border rounded disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      {isRecordingPayment && (
        <Modal
          onClose={() => setIsRecordingPayment(false)}
          disableOutsideClick={true}
        >
          <RecordPaymentForm
            bills={selectedBills}
            dealerName={selectedBills[0]?.dealerName}
            initialDate={selectedPaidBill?.date}
            initialMethod={selectedPaidBill?.paymentMethod}
            onClose={() => setIsRecordingPayment(false)}
            onSuccess={() => {
              setSelectedIds([]);
              onDeleteSuccess?.();
            }}
          />
        </Modal>
      )}

      {markPaidRow && (
        <Modal onClose={() => setMarkPaidRow(null)} disableOutsideClick={true}>
          <MarkPaidForm
            payment={markPaidRow}
            onClose={() => setMarkPaidRow(null)}
            onSuccess={onDeleteSuccess}
          />
        </Modal>
      )}
    </div>
  );
};

export default PaymentsTable;
